import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Building2, Eye, EyeOff, LogIn } from 'lucide-react';
import { checkLocationAccess } from '@/utils/gpsRestriction';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Clear any previous error messages
    setErrorMessage('');
    
    console.log('=== LOGIN ATTEMPT ===');
    console.log('Username:', username);
    console.log('Password length:', password.length);
    console.log('API endpoint:', `${API}/auth/login`);
    
    if (!username || !password) {
      console.error('Missing credentials');
      setErrorMessage('Please enter both username and password');
      toast.error('Please enter both username and password');
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('Sending login request...');
      const response = await axios.post(`${API}/auth/login`, {
        username: username,
        password: password
      });
      
      console.log('Login response received:', response.status);
      console.log('Token received:', response.data.token ? 'YES' : 'NO');
      console.log('User data:', response.data.user);
      
      const userData = response.data.user;
      
      // GPS LOCATION CHECK - Only for GPS-restricted users
      if (userData.gps_restricted) {
        console.log('🌍 GPS-restricted user - checking location...');
        toast.loading('Verifying location access...', { id: 'gps-check' });
        
        try {
          const locationResult = await checkLocationAccess();
          
          if (!locationResult.allowed) {
            console.error('❌ Location access denied:', locationResult.distance + 'm from office');
            console.error('Your coordinates:', locationResult.latitude, locationResult.longitude);
            console.error('Office coordinates: 24.830285°N, 67.0708655°E');
            console.error('Allowed radius: 200 meters');
            
            // Dismiss loading toast
            toast.dismiss('gps-check');
            
            // Set inline error message with debug info
            const debugMsg = `Access Denied: You are ${locationResult.distance}m from office (allowed: 200m). Your location: ${locationResult.latitude?.toFixed(4)}°N, ${locationResult.longitude?.toFixed(4)}°E`;
            setErrorMessage(debugMsg);
            
            // Show persistent error toast with distance info
            toast.error(
              `Access Denied: You are ${locationResult.distance} meters from the office. Maximum allowed distance is 200 meters.`,
              { 
                duration: 15000,
                position: 'top-center',
                style: {
                  background: '#dc2626',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  padding: '16px 24px'
                }
              }
            );
            setLoading(false);
            return; // Block login
          }
          
          console.log('✅ Location verified:', locationResult.distance + 'm from office');
          console.log('Your coordinates:', locationResult.latitude, locationResult.longitude);
          console.log('Office coordinates: 24.830285°N, 67.0708655°E');
          toast.success(`Location verified: ${locationResult.distance}m from office`, { id: 'gps-check' });
        } catch (locationError) {
          console.error('❌ GPS error:', locationError);
          console.error('Error details:', JSON.stringify(locationError, null, 2));
          
          // Dismiss loading toast
          toast.dismiss('gps-check');
          
          // Use the specific error message from the location check
          const errorMsg = locationError.error || 'This database is inaccessible outside permitted zone.';
          
          // Set inline error message
          setErrorMessage(errorMsg);
          
          // Show persistent error toast with specific error
          toast.error(
            errorMsg,
            { 
              duration: 15000,
              position: 'top-center',
              style: {
                background: '#dc2626',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '16px 24px'
              }
            }
          );
          setLoading(false);
          return; // Block login if GPS unavailable
        }
      } else {
        console.log('✅ User not GPS-restricted - login allowed from anywhere');
      }
      
      // Store user data with all permissions
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', response.data.token);
      
      console.log('Data stored in localStorage');
      console.log('Calling onLogin callback...');
      
      toast.success('Login successful!');
      onLogin(response.data.token, response.data.user);
      
      console.log('onLogin callback executed');
      console.log('Navigating to dashboard...');
      
      // Redirect to dashboard
      navigate('/dashboard');
      
    } catch (error) {
      console.error('=== LOGIN ERROR ===');
      console.error('Error object:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      // Show prominent error message for invalid credentials
      const errMsg = error.response?.data?.detail || 'Invalid username or password';
      
      // Set inline error message
      setErrorMessage(errMsg);
      
      // Show error toast
      toast.error(
        errMsg,
        { 
          duration: 8000,
          position: 'top-center',
          style: {
            background: '#dc2626',
            color: '#fff',
            fontSize: '16px',
            fontWeight: 'bold',
            padding: '16px 24px'
          }
        }
      );
    } finally {
      setLoading(false);
      console.log('Login attempt finished');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <Card className="w-full max-w-md shadow-2xl border-2 border-gray-700">
        <CardHeader className="space-y-4 text-center border-b-2 border-gray-200 pb-6">
          <div className="flex justify-center mb-2">
            <div className="w-20 h-20 rounded-lg bg-black border-2 border-white flex items-center justify-center">
              <Building2 className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            MNTX CITY OFFICE
          </CardTitle>
          <CardDescription className="text-base text-gray-600">
            User Access Control System
          </CardDescription>
        </CardHeader>
        
        <CardContent className="pt-6">
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Error Message Display */}
            {errorMessage && (
              <div className="p-4 bg-red-50 border-2 border-red-500 rounded-lg">
                <p className="text-red-700 font-semibold text-center text-sm">
                  ⚠️ {errorMessage}
                </p>
              </div>
            )}
            
            {/* Username Field */}
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-semibold uppercase tracking-wide">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="border-2 border-gray-300 focus:border-black h-11"
                data-testid="login-username-input"
                autoComplete="username"
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold uppercase tracking-wide">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-2 border-gray-300 focus:border-black h-11 pr-10"
                  data-testid="login-password-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <Button 
              type="submit" 
              className="w-full h-11 bg-black hover:bg-gray-800 text-white font-semibold text-base mt-6" 
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Logging in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="w-5 h-5" />
                  Login
                </span>
              )}
            </Button>
          </form>

          {/* Information Text */}
          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account? Contact your administrator.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
